import { arrayValue, isArray } from '../array.js';
import { isResolvedPolicySnapshot } from '../../../source/shared/period-policy.ts';
import type { ToolDefinition, ToolConfig, ToolId } from '../../types/tool-registry.js';
import type { Calculation, CoreMetadata, FinanceBusinessToolId } from '../../types/calculation-core.js';
import type { FinanceValues } from '../../../source/shared/calculation-types.js';
import type { FinanceConfig, FinanceInput } from '../calculation-core/finance-contracts.js';
type PolicyLifecycle = Extract<ToolConfig, {
    kind: 'compound-interest-v1';
}>['policyLifecycle'];
interface Options {
    readonly currentDate: string;
    readonly period?: import('../../../source/shared/period-policy.ts').ResolvedPolicySnapshot;
}
interface FinanceContract {
    readonly slug: string;
    readonly path: string;
    readonly categoryId: string;
    readonly classification: string;
    readonly engineId: string;
    readonly configKind: ToolConfig['kind'];
    readonly configKeys: readonly string[];
    readonly input: readonly (readonly [
        string,
        string,
        boolean
    ])[];
    readonly result: readonly (readonly [
        string,
        string,
        boolean
    ])[];
    readonly sources: readonly (readonly [
        string,
        string
    ])[];
    readonly topPolicySourceIds: readonly string[];
    readonly nestedPolicySourceIds: readonly string[] | null;
}
function isLifecycleShape(value: unknown): value is PolicyLifecycle {
    if (!isRecord(value))
        return false;
    const { configVersion, sourceIds, reviewedOn, effectiveFrom, effectiveTo, reviewAfter } = value;
    const ids = arrayValue(sourceIds);
    if (ids === null)
        return false;
    return typeof configVersion === 'string' && ids.every(id => typeof id === 'string') && typeof reviewedOn === 'string' && typeof effectiveFrom === 'string' && (effectiveTo === null || typeof effectiveTo === 'string') && (reviewAfter === null || typeof reviewAfter === 'string');
}
import { calculateCompoundInterest, calculateConsumerLoan, calculateDepreciationPlan, calculateDiscount, calculateHousingLoan, calculateInflationValue, calculatePosCommission, calculateRentIncrease, } from '@hephesapla/calculation-core';
import { getBrowserBundledPilotTool } from '@hephesapla/tool-registry/browser-contract';
export class FinancePolicyUnavailableError extends Error {
    readonly code: string;
    readonly toolId: string;
    readonly policyScope: string;
    constructor(code: string, toolId: string, policyScope: string, message: string) {
        super(message);
        this.code = code;
        this.toolId = toolId;
        this.policyScope = policyScope;
        this.name = 'FinancePolicyUnavailableError';
    }
}
const frozenFinanceContracts: Readonly<Partial<Record<ToolId, FinanceContract>>> = {
    'compound-interest': {
        slug: 'bilesik-faiz-hesaplama', path: '/bireysel-finans/bilesik-faiz-hesaplama', categoryId: 'bireysel-finans', classification: 'timeless',
        engineId: 'compound-interest-v1', configKind: 'compound-interest-v1',
        configKeys: ['annualRatePercent', 'kind', 'maximumTermYears', 'monthlyContribution', 'policy', 'policyLifecycle', 'principal', 'term'],
        input: [['principal', 'number', true], ['annualRatePercent', 'number', true], ['term', 'number', true], ['termUnit', 'select', true], ['compounding', 'radio', true], ['monthlyContribution', 'number', false]],
        result: [['principal', 'money', false], ['annualRatePercent', 'percentage', false], ['term', 'number', false], ['termUnit', 'text', false], ['termYears', 'number', false], ['compounding', 'text', false], ['monthlyContribution', 'money', false], ['contributionTiming', 'text', false], ['contributionPaymentCount', 'number', false], ['totalInvested', 'money', false], ['contributionFutureValue', 'money', false], ['totalInterest', 'money', false], ['futureValue', 'money', false], ['effectiveAnnualRatePercent', 'percentage', false], ['simpleInterestValue', 'money', false], ['compoundAdvantage', 'money', false], ['withholdingScenario', 'object', true], ['inflationScenario', 'object', true]],
        sources: [
            ['openstax-time-value-money', 'https://openstax.org/books/principles-finance/pages/7-3-methods-for-solving-time-value-of-money-problems'],
            ['tr-deposit-withholding', 'https://cdn.gib.gov.tr/api/gibportal-file/file/getFile?objectKey=DUYURU%2FUNIVERSAL%2F2026%2F2026_Gecici67.pdf'],
            ['tr-tufe-monthly-manual', 'https://veriportali.tuik.gov.tr/Bulten/Index?dil=1&p=Tuketici-Fiyat-Endeksi-Temmuz-2026-58297'],
        ],
        topPolicySourceIds: ['openstax-time-value-money'], nestedPolicySourceIds: null,
    },
    'inflation-value': {
        slug: 'enflasyon-hesaplama', path: '/bireysel-finans/enflasyon-hesaplama', categoryId: 'bireysel-finans', classification: 'timeless',
        engineId: 'inflation-value-v1', configKind: 'inflation-value-v1',
        configKeys: ['amount', 'annualInflationPercent', 'kind', 'policyLifecycle', 'years'],
        input: [['amount', 'number', true], ['annualInflationPercent', 'number', true], ['years', 'number', true]],
        result: [['amount', 'money', false], ['annualInflationPercent', 'percentage', false], ['years', 'number', false], ['cumulativeFactor', 'number', false], ['futureEquivalent', 'money', false], ['presentPurchasingPower', 'money', false], ['purchasingPowerLoss', 'money', false]],
        sources: [['tr-tufe-monthly-manual', 'https://veriportali.tuik.gov.tr/Bulten/Index?dil=1&p=Tuketici-Fiyat-Endeksi-Temmuz-2026-58297']],
        topPolicySourceIds: ['tr-tufe-monthly-manual'], nestedPolicySourceIds: null,
    },
    'housing-loan': {
        slug: 'konut-kredisi-hesaplama', path: '/bireysel-finans/konut-kredisi-hesaplama', categoryId: 'bireysel-finans', classification: 'timeless',
        engineId: 'housing-loan-v1', configKind: 'housing-loan-v1',
        configKeys: ['fileFeePercent', 'kind', 'monthlyRatePercent', 'policyLifecycle', 'principal', 'termMonths'],
        input: [['principal', 'number', true], ['monthlyRatePercent', 'number', true], ['termMonths', 'number', true], ['fileFeePercent', 'number', false]],
        result: [['principal', 'money', false], ['monthlyRatePercent', 'percentage', false], ['termMonths', 'number', false], ['fileFeePercent', 'percentage', false], ['monthlyPayment', 'money', false], ['totalInterest', 'money', false], ['totalPayment', 'money', false], ['fileFee', 'money', false], ['overallCost', 'money', false]],
        sources: [
            ['openstax-time-value-money', 'https://openstax.org/books/principles-finance/pages/7-3-methods-for-solving-time-value-of-money-problems'],
            ['tr-housing-credit-ltv', 'https://www.bddk.org.tr/Mevzuat/DokumanGetir/1327'],
        ],
        topPolicySourceIds: ['openstax-time-value-money'], nestedPolicySourceIds: null,
    },
    'consumer-loan': {
        slug: 'kredi-taksit-hesaplama', path: '/bireysel-finans/kredi-taksit-hesaplama', categoryId: 'bireysel-finans', classification: 'remote-config',
        engineId: 'consumer-loan-v1', configKind: 'consumer-loan-v1',
        configKeys: ['kind', 'monthlyRatePercent', 'policy', 'policyLifecycle', 'principal', 'termMonths'],
        input: [['principal', 'number', true], ['monthlyRatePercent', 'number', true], ['termMonths', 'number', true], ['mode', 'radio', true]],
        result: [['principal', 'money', false], ['monthlyRatePercent', 'percentage', false], ['termMonths', 'number', false], ['mode', 'text', false], ['effectiveMonthlyRatePercent', 'percentage', false], ['monthlyPayment', 'money', false], ['totalInterestAndTaxes', 'money', false], ['pureInterestAmount', 'money', false], ['totalPayment', 'money', false], ['bsmvAmount', 'money', false], ['kkdfAmount', 'money', false]],
        sources: [
            ['openstax-time-value-money', 'https://openstax.org/books/principles-finance/pages/7-3-methods-for-solving-time-value-of-money-problems'],
            ['tr-consumer-credit-taxes', 'https://gib.gov.tr/mevzuat/kanun/445/bkk/1849'],
            ['tr-consumer-credit-kkdf', 'https://cdn.gib.gov.tr/api/gibportal-file/file/getFileResources?objectKey=arsiv%2Fyardim-kaynaklar%2Fyararli-bilgiler%2Fkkdf-oranlari.pdf'],
            ['tr-consumer-credit-term', 'https://www.bddk.org.tr/Duyuru/EkGetir/2101?ekId=866'],
        ],
        topPolicySourceIds: ['openstax-time-value-money', 'tr-consumer-credit-taxes', 'tr-consumer-credit-kkdf', 'tr-consumer-credit-term'], nestedPolicySourceIds: null,
    },
    'depreciation-plan': {
        slug: 'amortisman-hesaplama', path: '/ticari-muhasebe/amortisman-hesaplama', categoryId: 'ticari-muhasebe', classification: 'timeless',
        engineId: 'depreciation-plan-v1', configKind: 'depreciation-plan-v1',
        configKeys: ['cost', 'kind', 'policy', 'policyLifecycle'],
        input: [['cost', 'number', true], ['usefulLifeYears', 'number', true], ['method', 'radio', true], ['decliningBalanceEligibilityConfirmed', 'boolean', false]],
        result: [['cost', 'money', false], ['usefulLifeYears', 'number', false], ['method', 'text', false], ['depreciationRatePercent', 'percentage', false], ['firstYearExpense', 'money', false], ['totalDepreciation', 'money', false], ['schedule', 'object-list', false]],
        sources: [
            ['tr-vuk-depreciation', 'https://cdn.gib.gov.tr/api/gibportal-file/file/getFileResources?objectKey=arsiv%2Fyardim-kaynaklar%2Fyararli-bilgiler%2FAmortismanOranlariTablosu.pdf'],
            ['tr-vuk-depreciation-methods', 'https://www.gib.gov.tr/mevzuat/kanun/434'],
        ],
        topPolicySourceIds: ['tr-vuk-depreciation'], nestedPolicySourceIds: ['tr-vuk-depreciation-methods'],
    },
    discount: {
        slug: 'indirim-iskonto-hesaplama', path: '/ticari-muhasebe/indirim-iskonto-hesaplama', categoryId: 'ticari-muhasebe', classification: 'timeless',
        engineId: 'discount-v1', configKind: 'discount-v1',
        configKeys: ['discountPercent', 'kind', 'policyLifecycle', 'price'],
        input: [['mode', 'radio', true], ['originalPrice', 'number', true], ['discountPercent', 'number', true], ['discountedPrice', 'number', true]],
        result: [['mode', 'text', false], ['originalPrice', 'money', false], ['discountPercent', 'percentage', false], ['discountAmount', 'money', false], ['discountedPrice', 'money', false]],
        sources: [['tr-discount-reference-price', 'https://ticaret.gov.tr/haberler/ticaret-bakanligindan-indirimli-satis-reklamlarinda-seffafligi-artiracak-yeni-duzenleme']],
        topPolicySourceIds: ['tr-discount-reference-price'], nestedPolicySourceIds: null,
    },
    'rent-increase': {
        slug: 'kira-artis-orani-hesaplama', path: '/ticari-muhasebe/kira-artis-orani-hesaplama', categoryId: 'ticari-muhasebe', classification: 'timeless',
        engineId: 'rent-increase-v1', configKind: 'rent-increase-v1',
        configKeys: ['currentMonthlyRent', 'kind', 'policy', 'policyLifecycle', 'twelveMonthCpiAveragePercent'],
        input: [['currentMonthlyRent', 'number', true], ['twelveMonthCpiAveragePercent', 'number', true]],
        result: [['currentMonthlyRent', 'money', false], ['twelveMonthCpiAveragePercent', 'percentage', false], ['monthlyIncrease', 'money', false], ['newMonthlyRent', 'money', false], ['annualExtraCost', 'money', false]],
        sources: [
            ['tr-rent-increase-tufe', 'https://www.mevzuat.gov.tr/mevzuatmetin/1.5.6098.pdf'],
            ['tr-tufe-monthly-manual', 'https://veriportali.tuik.gov.tr/Bulten/Index?dil=1&p=Tuketici-Fiyat-Endeksi-Temmuz-2026-58297'],
        ],
        topPolicySourceIds: ['tr-rent-increase-tufe', 'tr-tufe-monthly-manual'], nestedPolicySourceIds: null,
    },
    'pos-commission': {
        slug: 'pos-komisyonu-hesaplama', path: '/ticari-muhasebe/pos-komisyonu-hesaplama', categoryId: 'ticari-muhasebe', classification: 'timeless',
        engineId: 'pos-commission-v1', configKind: 'pos-commission-v1',
        configKeys: ['amount', 'commissionPercent', 'kind', 'policy', 'policyLifecycle', 'supportedInstallments'],
        input: [['amount', 'number', true], ['installmentCount', 'select', true], ['commissionPercent', 'number', true], ['mode', 'radio', true]],
        result: [['amount', 'money', false], ['installmentCount', 'number', false], ['commissionPercent', 'percentage', false], ['mode', 'text', false], ['commissionAmount', 'money', false], ['bsmvAmount', 'money', false], ['totalDeduction', 'money', false], ['netAmount', 'money', false], ['netAllocationPerInstallment', 'money', false]],
        sources: [
            ['tr-pos-commission', 'https://www.tcmb.gov.tr/wps/wcm/connect/tr/tcmb%2Btr/main%2Bmenu/istatistikler/bankacilik%2Bverileri/uye%2Bisyerlerine%2Buygulanacak%2Bazami%2Bkomisyon%2Boranlari'],
            ['tr-pos-merchant-commission-bsmv-scope', 'https://gib.gov.tr/mevzuat/kanun/436/ozelge/29405'],
            ['tr-general-bsmv-rate', 'https://gib.gov.tr/mevzuat/kanun/445/bkk/1849'],
        ],
        topPolicySourceIds: ['tr-pos-commission'], nestedPolicySourceIds: ['tr-pos-merchant-commission-bsmv-scope', 'tr-general-bsmv-rate'],
    },
};
const strictIsoDatePattern = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
const strictSemverPattern = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$/;
const policyLifecycleKeys = ['configVersion', 'effectiveFrom', 'effectiveTo', 'reviewAfter', 'reviewedOn', 'sourceIds'];
const BASELINE_FINANCE_CONFIG_VERSION = '1.0.0';
const FORWARD_CONFIG_TOOL_IDS = new Set([
    'consumer-loan',
    'depreciation-plan',
    'pos-commission',
]);
const FINANCE_MUTABLE_PROJECTION_VALUE = '<signed-policy-renewal>';
function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
    return value !== null && typeof value === 'object' && !isArray(value);
}
function sameProjection(left: unknown, right: unknown) {
    return JSON.stringify(left) === JSON.stringify(right);
}
function canonicalProjection(value: unknown): unknown {
    const entries = arrayValue(value);
    if (entries !== null)
        return entries.map(canonicalProjection);
    if (isRecord(value)) {
        return Object.fromEntries(Object.entries(value)
            .sort(([left], [right]) => (left < right ? -1 : left > right ? 1 : 0))
            .map(([key, child]) => [key, canonicalProjection(child)]));
    }
    return value;
}
function sameImmutableProjection(left: unknown, right: unknown) {
    return JSON.stringify(canonicalProjection(left)) === JSON.stringify(canonicalProjection(right));
}
function lifecycleImmutableProjection(lifecycle: PolicyLifecycle) {
    return { sourceIds: lifecycle.sourceIds };
}
function configImmutableProjection(config: ToolConfig) {
    if (!isFinanceConfig(config))
        throw new Error('Finans config yaşam döngüsü eksik.');
    const topLevel = {
        ...config,
        policyLifecycle: lifecycleImmutableProjection(config.policyLifecycle),
    };
    if (config.kind === 'consumer-loan-v1') {
        return {
            ...topLevel,
            policy: {
                bsmvRate: FINANCE_MUTABLE_PROJECTION_VALUE,
                kkdfRate: FINANCE_MUTABLE_PROJECTION_VALUE,
                maximumTermTiers: FINANCE_MUTABLE_PROJECTION_VALUE,
            },
        };
    }
    if (config.kind === 'depreciation-plan-v1') {
        return {
            ...topLevel,
            policy: {
                decliningBalanceMultiplier: FINANCE_MUTABLE_PROJECTION_VALUE,
                maximumDecliningRate: FINANCE_MUTABLE_PROJECTION_VALUE,
                policyLifecycle: lifecycleImmutableProjection(config.policy.policyLifecycle),
            },
        };
    }
    if (config.kind === 'pos-commission-v1') {
        return {
            ...topLevel,
            policy: {
                bsmvRate: FINANCE_MUTABLE_PROJECTION_VALUE,
                policyLifecycle: lifecycleImmutableProjection(config.policy.policyLifecycle),
            },
        };
    }
    return topLevel;
}
function toolImmutableProjection(tool: ToolDefinition) {
    return {
        ...tool,
        configVersion: FINANCE_MUTABLE_PROJECTION_VALUE,
        effectiveFrom: FINANCE_MUTABLE_PROJECTION_VALUE,
        effectiveTo: FINANCE_MUTABLE_PROJECTION_VALUE,
        lastReviewed: FINANCE_MUTABLE_PROJECTION_VALUE,
        updateCadence: {
            ...tool.updateCadence,
            reviewAfter: FINANCE_MUTABLE_PROJECTION_VALUE,
        },
        sources: tool.sources.map((source) => ({
            ...source,
            reviewedOn: FINANCE_MUTABLE_PROJECTION_VALUE,
        })),
        config: configImmutableProjection(tool.config),
    };
}
function sortedKeys(value: object) {
    return Object.keys(value).sort();
}
function isValidIsoDate(value: unknown): value is string {
    if (typeof value !== 'string' || !strictIsoDatePattern.test(value))
        return false;
    const parsed = new Date(`${value}T00:00:00.000Z`);
    return !Number.isNaN(parsed.valueOf()) && parsed.toISOString().slice(0, 10) === value;
}
function compareStrictSemver(left: string, right: string) {
    const leftMatch = strictSemverPattern.exec(left);
    const rightMatch = strictSemverPattern.exec(right);
    if (leftMatch === null || rightMatch === null)
        return null;
    for (let index = 1; index <= 3; index += 1) {
        const leftPart = BigInt(leftMatch[index]);
        const rightPart = BigInt(rightMatch[index]);
        if (leftPart < rightPart)
            return -1;
        if (leftPart > rightPart)
            return 1;
    }
    return 0;
}
function inputProjection(tool: ToolDefinition) {
    return tool.inputSchema.fields.map((field) => [field.id, field.type, field.required]);
}
function resultProjection(tool: ToolDefinition) {
    return tool.resultSchema.fields.map((field) => [field.id, field.type, field.nullable]);
}
function contractFor(tool: ToolDefinition) {
    const contract = frozenFinanceContracts[tool.toolId];
    if (contract === undefined) {
        throw new Error(`${tool.toolId} finans/işletme runtime adapterı tarafından desteklenmiyor.`);
    }
    const canonicalToolId = `${tool.category.id}.${tool.slug}`;
    if (tool.slug !== contract.slug ||
        tool.path !== contract.path ||
        tool.category.id !== contract.categoryId ||
        tool.classification !== contract.classification ||
        tool.engineId !== contract.engineId ||
        tool.config.kind !== contract.configKind ||
        tool.schemaVersion !== '1.0.0' ||
        tool.contentVersion !== '1.0.0' ||
        tool.minAppVersion !== '0.3.0' ||
        tool.testVectors.kind !== 'finance-business-oracle-v1' ||
        canonicalToolId !== `${contract.categoryId}.${contract.slug}`) {
        throw new Error(`${tool.toolId} frozen finans/işletme kimlik sözleşmesiyle eşleşmiyor.`);
    }
    const versionOrder = compareStrictSemver(tool.configVersion, BASELINE_FINANCE_CONFIG_VERSION);
    const forwardConfig = FORWARD_CONFIG_TOOL_IDS.has(tool.toolId);
    if (versionOrder === null || (forwardConfig ? versionOrder < 0 : versionOrder !== 0)) {
        throw new Error(forwardConfig
            ? `${tool.toolId}.configVersion strict semver ve baseline ${BASELINE_FINANCE_CONFIG_VERSION} veya yeni olmalıdır.`
            : `${tool.toolId}.configVersion zamansız saf sözleşme için exact ${BASELINE_FINANCE_CONFIG_VERSION} olmalıdır.`);
    }
    if (!sameProjection(sortedKeys(tool.config), contract.configKeys)) {
        throw new Error(`${tool.toolId} config alan sözleşmesiyle eşleşmiyor.`);
    }
    if (!sameProjection(inputProjection(tool), contract.input)) {
        throw new Error(`${tool.toolId} input descriptor sözleşmesiyle eşleşmiyor.`);
    }
    if (!sameProjection(resultProjection(tool), contract.result)) {
        throw new Error(`${tool.toolId} result descriptor sözleşmesiyle eşleşmiyor.`);
    }
    const sourceProjection = tool.sources.map((source) => [source.sourceId, source.url]);
    if (!sameProjection(sourceProjection, contract.sources)) {
        throw new Error(`${tool.toolId} kaynak kimliği/URL sözleşmesiyle eşleşmiyor.`);
    }
    const bundledBaseline = getBrowserBundledPilotTool(tool.toolId);
    if (bundledBaseline === undefined ||
        !sameImmutableProjection(toolImmutableProjection(tool), toolImmutableProjection(bundledBaseline))) {
        throw new Error(`${tool.toolId} ileri sürümü immutable ekran/içerik/kaynak/config yapısını değiştiremez.`);
    }
    return contract;
}
function policyError(code: string, toolId: ToolId, scope: string, message: string): never {
    throw new FinancePolicyUnavailableError(code, toolId, scope, message);
}
function validateLifecycle(lifecycle: unknown, tool: ToolDefinition, scope: string, expectedSourceIds: readonly string[], bindToolDates: boolean = false): PolicyLifecycle {
    if (!isLifecycleShape(lifecycle) || !sameProjection(sortedKeys(lifecycle), policyLifecycleKeys)) {
        return policyError('POLICY_INVALID', tool.toolId, scope, 'Policy yaşam döngüsü alanları geçersizdir.');
    }
    const typed = lifecycle;
    if (typed.configVersion !== tool.configVersion ||
        typed.reviewedOn !== tool.lastReviewed ||
        typed.effectiveFrom !== tool.effectiveFrom ||
        !isValidIsoDate(typed.reviewedOn) ||
        !isValidIsoDate(typed.effectiveFrom) ||
        (typed.effectiveTo !== null && !isValidIsoDate(typed.effectiveTo)) ||
        (typed.reviewAfter !== null && !isValidIsoDate(typed.reviewAfter)) ||
        !sameProjection(typed.sourceIds, expectedSourceIds) ||
        typed.sourceIds.some((sourceId) => !tool.sources.some((source) => source.sourceId === sourceId)) ||
        typed.sourceIds.some((sourceId) => tool.sources.find((source) => source.sourceId === sourceId)?.reviewedOn !== typed.reviewedOn) ||
        (bindToolDates && typed.effectiveTo !== tool.effectiveTo) ||
        (bindToolDates && typed.reviewAfter !== tool.updateCadence.reviewAfter) ||
        (typed.effectiveTo !== null && typed.effectiveTo < typed.effectiveFrom) ||
        (typed.reviewAfter !== null && typed.reviewAfter <= typed.reviewedOn) ||
        (tool.toolId !== 'consumer-loan' && tool.toolId !== 'pos-commission' && typed.reviewAfter !== null && typed.effectiveTo !== null && typed.reviewAfter <= typed.effectiveTo)) {
        return policyError('POLICY_INVALID', tool.toolId, scope, 'Policy yaşam döngüsü sürüm, tarih veya kaynak bağı geçersizdir.');
    }
    return typed;
}
function assertPolicyActive(lifecycle: PolicyLifecycle, toolId: ToolId, scope: string, currentDate: string, reviewBlocks: boolean = true) {
    if (currentDate < lifecycle.effectiveFrom) {
        policyError('POLICY_NOT_YET_EFFECTIVE', toolId, scope, `Doğrulanmış politika ${lifecycle.effectiveFrom} tarihinde yürürlüğe girecek.`);
    }
    if (lifecycle.effectiveTo !== null && currentDate > lifecycle.effectiveTo) {
        policyError('POLICY_EXPIRED', toolId, scope, `Doğrulanmış politika ${lifecycle.effectiveTo} tarihinde sona erdi.`);
    }
    if (reviewBlocks && lifecycle.reviewAfter !== null && currentDate >= lifecycle.reviewAfter) {
        policyError('POLICY_REVIEW_REQUIRED', toolId, scope, `Doğrulanmış politikanın ${lifecycle.reviewAfter} tarihli incelemesi tamamlanmalıdır.`);
    }
}
function selectedLifecycle(tool: ToolDefinition, contract: FinanceContract, input: unknown, options: Options) {
    if (!isValidIsoDate(options.currentDate)) {
        return policyError('POLICY_INVALID', tool.toolId, 'method', 'currentDate geçerli YYYY-AA-GG olmalıdır.');
    }
    if (options.period && (!isResolvedPolicySnapshot(options.period) || options.period.toolId !== tool.toolId || options.period.auditDate !== options.currentDate || options.period.policyRevisionId !== tool.configVersion)) {
        return policyError('POLICY_INVALID', tool.toolId, 'method', 'Çözümlenmiş dönem ve yapılandırma uyuşmuyor.');
    }
    const config = tool.config;
    if (!('policyLifecycle' in config))
        return policyError('POLICY_INVALID', tool.toolId, 'method', 'Policy yaşam döngüsü alanları geçersizdir.');
    const top = validateLifecycle(config.policyLifecycle, tool, contract.classification === 'remote-config' ? 'tool' : 'method', contract.topPolicySourceIds, true);
    const inputRecord = isRecord(input) ? input : null;
    const modeId = tool.toolId === 'depreciation-plan' ? inputRecord?.method === 'declining-balance' ? 'declining-balance' : 'straight-line' : tool.toolId === 'pos-commission' ? inputRecord?.mode === 'commission-plus-bsmv' ? 'commission-plus-bsmv' : 'commission-only' : 'all';
    if (options.period && options.period.modeId !== modeId) policyError('POLICY_INVALID', tool.toolId, 'method', 'Çözümlenmiş dönem ve hesaplama modu uyuşmuyor.');
    if (tool.toolId === 'consumer-loan') {
        assertPolicyActive(top, 'consumer-loan', 'tool', options.currentDate, false);
        return { lifecycle: top, scope: 'tool' };
    }
    if (tool.toolId === 'depreciation-plan' && config.kind === 'depreciation-plan-v1' && inputRecord?.method === 'declining-balance') {
        const nested = config.policy.policyLifecycle;
        const lifecycle = validateLifecycle(nested, tool, 'declining-balance', contract.nestedPolicySourceIds ?? []);
        if (options.period) {
            const period = options.period;
            if (period.modeId !== 'declining-balance' || period.selection.kind !== 'year' || period.periodId < lifecycle.effectiveFrom.slice(0, 4) || (lifecycle.effectiveTo !== null && period.periodId > lifecycle.effectiveTo.slice(0, 4))) policyError('POLICY_INVALID', tool.toolId, 'declining-balance', 'Dönem ve yapılandırma uyuşmuyor.');
        } else assertPolicyActive(lifecycle, 'depreciation-plan', 'declining-balance', options.currentDate);
        return { lifecycle, scope: 'declining-balance' };
    }
    if (tool.toolId === 'pos-commission' && config.kind === 'pos-commission-v1' && inputRecord?.mode === 'commission-plus-bsmv') {
        const nested = config.policy.policyLifecycle;
        const lifecycle = validateLifecycle(nested, tool, 'commission-plus-bsmv', contract.nestedPolicySourceIds ?? []);
        assertPolicyActive(lifecycle, 'pos-commission', 'commission-plus-bsmv', options.currentDate, false);
        return { lifecycle, scope: 'commission-plus-bsmv' };
    }
    return { lifecycle: top, scope: 'method' };
}
function metadataFor(tool: ToolDefinition, lifecycle: PolicyLifecycle): CoreMetadata {
    return {
        calculatorId: tool.toolId,
        version: lifecycle.configVersion,
        effectiveFrom: lifecycle.effectiveFrom,
        ...(lifecycle.effectiveTo === null ? {} : { effectiveTo: lifecycle.effectiveTo }),
        lastVerifiedAt: lifecycle.reviewedOn,
        sources: lifecycle.sourceIds.map((sourceId) => {
            const source = tool.sources.find((candidate) => candidate.sourceId === sourceId);
            if (source === undefined) {
                return policyError('POLICY_INVALID', tool.toolId, 'method', `Policy kaynağı çözümlenemedi: ${sourceId}.`);
            }
            return {
                id: source.sourceId,
                title: source.title,
                publisher: source.publisher,
                url: source.url,
                verifiedAt: source.reviewedOn,
            };
        }),
        notes: tool.content.limitations,
    };
}
function closedConfigFor(tool: ToolDefinition, lifecycle: PolicyLifecycle): FinanceConfig | undefined {
    const config = tool.config;
    const metadata = metadataFor(tool, lifecycle);
    switch (config.kind) {
        case 'compound-interest-v1':
            return { metadata, principal: config.principal, annualRatePercent: config.annualRatePercent, term: config.term, maximumTermYears: config.maximumTermYears, monthlyContribution: config.monthlyContribution, policy: config.policy };
        case 'inflation-value-v1':
            return { metadata, amount: config.amount, annualInflationPercent: config.annualInflationPercent, years: config.years };
        case 'housing-loan-v1':
            return { metadata, principal: config.principal, monthlyRatePercent: config.monthlyRatePercent, termMonths: config.termMonths, fileFeePercent: config.fileFeePercent };
        case 'consumer-loan-v1':
            return { metadata, principal: config.principal, monthlyRatePercent: config.monthlyRatePercent, termMonths: config.termMonths, policy: config.policy };
        case 'depreciation-plan-v1':
            return { metadata, cost: config.cost, policy: { decliningBalanceMultiplier: config.policy.decliningBalanceMultiplier, maximumDecliningRate: config.policy.maximumDecliningRate } };
        case 'discount-v1':
            return { metadata, price: config.price, discountPercent: config.discountPercent };
        case 'rent-increase-v1':
            return { metadata, currentMonthlyRent: config.currentMonthlyRent, twelveMonthCpiAveragePercent: config.twelveMonthCpiAveragePercent, policy: config.policy };
        case 'pos-commission-v1':
            return { metadata, amount: config.amount, commissionPercent: config.commissionPercent, supportedInstallments: config.supportedInstallments, policy: { bsmvRate: config.policy.bsmvRate } };
    }
}
function receiptFor(tool: ToolDefinition, contract: FinanceContract, lifecycle: PolicyLifecycle, scope: string, result: Calculation<unknown>) {
    if (result.metadata.calculatorId !== tool.toolId || result.metadata.version !== lifecycle.configVersion) {
        throw new Error(`${tool.toolId} sonuç metadata kaydı seçilen policy lifecycle ile eşleşmiyor.`);
    }
    const sourceIds = result.metadata.sources.map((source) => source.id);
    if (!sameProjection(sourceIds, lifecycle.sourceIds)) {
        throw new Error(`${tool.toolId} sonuç provenance kaydı seçilen policy kaynaklarıyla eşleşmiyor.`);
    }
    return {
        runtimeToolId: tool.toolId,
        canonicalToolId: `${contract.categoryId}.${contract.slug}`,
        calculatorId: result.metadata.calculatorId,
        engineId: contract.engineId,
        configVersion: result.metadata.version,
        effectiveFrom: result.metadata.effectiveFrom,
        lastReviewed: result.metadata.lastVerifiedAt,
        sourceIds,
        policyAudit: {
            scope,
            configVersion: lifecycle.configVersion,
            sourceIds: lifecycle.sourceIds,
            reviewedOn: lifecycle.reviewedOn,
            effectiveFrom: lifecycle.effectiveFrom,
            effectiveTo: lifecycle.effectiveTo,
            reviewAfter: lifecycle.reviewAfter,
        },
        resultMetadata: result.metadata,
    };
}
export function financeBusinessConfigFor(tool: ToolDefinition, input: unknown, options: Options) {
    const contract = contractFor(tool);
    const { lifecycle } = selectedLifecycle(tool, contract, input, options);
    return closedConfigFor(tool, lifecycle);
}
export function calculateFinanceBusinessTool<T extends FinanceBusinessToolId>(tool: ToolDefinition & {
    readonly toolId: T;
}, input: FinanceInput<T>, options: Options) {
    const contract = contractFor(tool);
    const { lifecycle, scope } = selectedLifecycle(tool, contract, input, options);
    const result = financeCalculators[tool.toolId](tool, input, lifecycle);
    const receipt = receiptFor(tool, contract, lifecycle, scope, result);
    return { result, receipt: options.period ? { ...receipt, periodId: options.period.periodId, modeId: options.period.modeId, policyRevisionId: options.period.policyRevisionId, auditDate: options.period.auditDate } : receipt };
}
const financeCalculators: {
    readonly [K in FinanceBusinessToolId]: (tool: ToolDefinition, input: FinanceInput<K>, lifecycle: PolicyLifecycle) => Calculation<FinanceValues[K]>;
} = {
    'compound-interest': (tool, input, lifecycle) => {
        const config = closedConfigFor(tool, lifecycle);
        if (!config || !('annualRatePercent' in config))
            throw new Error(`${tool.toolId} core config sözleşmesi eşleşmiyor.`);
        return calculateCompoundInterest(input, config);
    },
    'inflation-value': (tool, input, lifecycle) => {
        const config = closedConfigFor(tool, lifecycle);
        if (!config || !('annualInflationPercent' in config))
            throw new Error(`${tool.toolId} core config sözleşmesi eşleşmiyor.`);
        return calculateInflationValue(input, config);
    },
    'housing-loan': (tool, input, lifecycle) => {
        const config = closedConfigFor(tool, lifecycle);
        if (!config || !('fileFeePercent' in config))
            throw new Error(`${tool.toolId} core config sözleşmesi eşleşmiyor.`);
        return calculateHousingLoan(input, config);
    },
    'consumer-loan': (tool, input, lifecycle) => {
        const config = closedConfigFor(tool, lifecycle);
        if (!config || !('monthlyRatePercent' in config && 'policy' in config))
            throw new Error(`${tool.toolId} core config sözleşmesi eşleşmiyor.`);
        return calculateConsumerLoan(input, config);
    },
    'depreciation-plan': (tool, input, lifecycle) => {
        const config = closedConfigFor(tool, lifecycle);
        if (!config || !('cost' in config))
            throw new Error(`${tool.toolId} core config sözleşmesi eşleşmiyor.`);
        return calculateDepreciationPlan(input, config);
    },
    'discount': (tool, input, lifecycle) => {
        const config = closedConfigFor(tool, lifecycle);
        if (!config || !('price' in config))
            throw new Error(`${tool.toolId} core config sözleşmesi eşleşmiyor.`);
        return calculateDiscount(input, config);
    },
    'rent-increase': (tool, input, lifecycle) => {
        const config = closedConfigFor(tool, lifecycle);
        if (!config || !('currentMonthlyRent' in config))
            throw new Error(`${tool.toolId} core config sözleşmesi eşleşmiyor.`);
        return calculateRentIncrease(input, config);
    },
    'pos-commission': (tool, input, lifecycle) => {
        const config = closedConfigFor(tool, lifecycle);
        if (!config || !('commissionPercent' in config))
            throw new Error(`${tool.toolId} core config sözleşmesi eşleşmiyor.`);
        return calculatePosCommission(input, config);
    }
};

function isFinanceConfig(config: ToolConfig): config is Extract<ToolConfig,{kind: 'compound-interest-v1' | 'inflation-value-v1' | 'housing-loan-v1' | 'consumer-loan-v1' | 'depreciation-plan-v1' | 'discount-v1' | 'rent-increase-v1' | 'pos-commission-v1'}> {
    return config.kind === 'compound-interest-v1' || config.kind === 'inflation-value-v1' || config.kind === 'housing-loan-v1' || config.kind === 'consumer-loan-v1' || config.kind === 'depreciation-plan-v1' || config.kind === 'discount-v1' || config.kind === 'rent-increase-v1' || config.kind === 'pos-commission-v1';
}
